const DEFAULT_PROD_SITE_URL = 'https://barabom.me';
const DEFAULT_STAGING_SITE_URL = 'https://stg.barabom.me';
const POOP_DATA_PATH_SUFFIX = '/poop-data';

export const POOP_DATA_SUPPORTED_LOCALES = ['ko', 'ja', 'en'] as const;

export type PoopDataLocale = (typeof POOP_DATA_SUPPORTED_LOCALES)[number];
export type PoopDataHrefLang = PoopDataLocale | 'x-default';

export type PoopDataAlternateLink = {
  hrefLang: PoopDataHrefLang;
  href: string;
};

const POOP_DATA_LOCALIZED_PATHS: Record<PoopDataLocale, string> = {
  ko: `/ko${POOP_DATA_PATH_SUFFIX}`,
  ja: `/ja${POOP_DATA_PATH_SUFFIX}`,
  en: `/en${POOP_DATA_PATH_SUFFIX}`,
};

const POOP_DATA_ROUTE_PATHS = new Set<string>([
  POOP_DATA_PATH_SUFFIX,
  ...POOP_DATA_SUPPORTED_LOCALES.map((locale) => POOP_DATA_LOCALIZED_PATHS[locale]),
]);

const normalizeSiteUrl = (siteUrl: string) => siteUrl.replace(/\/$/, '');

const normalizePathname = (pathname: string) => {
  const [pathOnly] = pathname.split(/[?#]/);

  return pathOnly.length > 1 ? pathOnly.replace(/\/$/, '') : pathOnly;
};

export const isPoopDataLocale = (locale: string): locale is PoopDataLocale =>
  POOP_DATA_SUPPORTED_LOCALES.includes(locale as PoopDataLocale);

export const assertPoopDataLocale = (locale: string): PoopDataLocale => {
  if (isPoopDataLocale(locale)) {
    return locale;
  }

  throw new RangeError(`Unsupported poop-data locale: ${locale}`);
};

export const getPoopDataSiteUrl = () => {
  const configuredSiteUrl = import.meta.env.VITE_SITE_URL;
  const fallbackSiteUrl = import.meta.env.MODE === 'prod' ? DEFAULT_PROD_SITE_URL : DEFAULT_STAGING_SITE_URL;

  return normalizeSiteUrl(configuredSiteUrl || fallbackSiteUrl);
};

export const getPoopDataLocalizedPath = (locale: string) => {
  const supportedLocale = assertPoopDataLocale(locale);

  return POOP_DATA_LOCALIZED_PATHS[supportedLocale];
};

export const getPoopDataLanguageSwitchPath = (pathname: string, locale: string) => {
  const normalizedPathname = normalizePathname(pathname);

  if (!POOP_DATA_ROUTE_PATHS.has(normalizedPathname)) {
    return null;
  }

  return getPoopDataLocalizedPath(locale);
};

export const getPoopDataLocaleFromPathname = (pathname: string): PoopDataLocale | null => {
  const normalizedPathname = normalizePathname(pathname);
  const matchedLocale = POOP_DATA_SUPPORTED_LOCALES.find(
    (locale) => POOP_DATA_LOCALIZED_PATHS[locale] === normalizedPathname
  );

  return matchedLocale ?? null;
};

export const getPoopDataCanonicalUrl = (locale: string, siteUrl = getPoopDataSiteUrl()) =>
  `${normalizeSiteUrl(siteUrl)}${getPoopDataLocalizedPath(locale)}`;

export const getPoopDataAlternateLinks = (siteUrl = getPoopDataSiteUrl()): PoopDataAlternateLink[] => {
  const normalizedSiteUrl = normalizeSiteUrl(siteUrl);
  const localeAlternates = POOP_DATA_SUPPORTED_LOCALES.map((locale) => ({
    hrefLang: locale,
    href: `${normalizedSiteUrl}${POOP_DATA_LOCALIZED_PATHS[locale]}`,
  }));

  return [
    ...localeAlternates,
    {
      hrefLang: 'x-default',
      href: `${normalizedSiteUrl}${POOP_DATA_LOCALIZED_PATHS.en}`,
    },
  ];
};

export const getPoopDataSeoUrls = (locale: string, siteUrl = getPoopDataSiteUrl()) => ({
  path: getPoopDataLocalizedPath(locale),
  canonicalUrl: getPoopDataCanonicalUrl(locale, siteUrl),
  alternates: getPoopDataAlternateLinks(siteUrl),
});
